import { useState, useEffect, useCallback } from "react";
import {
  Trash2, ChevronLeft, ChevronRight, Search, Plus,
  Eye, Pencil, X, Save, Loader2, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_API_URL}/api`;
const PLAN_COLORS = { free: "#6b7280", starter: "#10b981", pro: "#3b82f6", business: "#a855f7", unlimited: "#f59e0b" };
const DEFAULT_PLANS = ["free", "starter", "pro", "business", "unlimited"];
const PER_PAGE = 20;

const getPlanColor = (planId) => PLAN_COLORS[planId] || "#22d3ee";

function Pagination({ page, pages, onPage }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={() => onPage(page - 1)} disabled={page <= 1}><ChevronLeft className="w-3.5 h-3.5" /></Button>
      <span className="text-xs font-mono text-muted-foreground">{page} / {pages}</span>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={() => onPage(page + 1)} disabled={page >= pages}><ChevronRight className="w-3.5 h-3.5" /></Button>
    </div>
  );
}

function UserModal({ userId, mode: initialMode, onClose, onSaved, t, planOptions }) {
  const [mode, setMode] = useState(initialMode);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(mode !== "create");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", plan: "free", role: "user", usage_count: 0 });
  const selectablePlans = Array.isArray(planOptions) && planOptions.length ? planOptions : DEFAULT_PLANS;

  useEffect(() => {
    if (mode === "create") return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/users/${userId}/detail`, { withCredentials: true });
        setDetail(res.data);
        setForm({ name: res.data.name || "", email: res.data.email || "", password: "", plan: res.data.plan || "free", role: res.data.role || "user", usage_count: res.data.usage_count || 0 });
      } catch { toast.error(t("admin.usersPage.loadDetailFail")); }
      finally { setLoading(false); }
    };
    load();
  }, [userId, mode, t]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (mode === "create") {
        if (!form.name.trim() || !form.email.trim() || !form.password) { toast.error(t("admin.usersPage.requiredFields")); setSaving(false); return; }
        await axios.post(`${API}/admin/users`, form, { withCredentials: true });
        toast.success(t("admin.usersPage.createSuccess"));
      } else {
        const payload = { name: form.name, plan: form.plan, role: form.role, usage_count: Number(form.usage_count) };
        await axios.patch(`${API}/admin/users/${userId}`, payload, { withCredentials: true });
        toast.success(t("admin.usersPage.updateSuccess"));
      }
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.usersPage.saveFail")); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const planBadge = (plan) => (<span className="text-xs font-mono capitalize" style={{ color: getPlanColor(plan || "free") }}>{plan || "free"}</span>);
  const fmt = (v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(v || 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 12 }}
        className="relative bg-background border border-border rounded-md shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" data-testid="user-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-background z-10">
          <div>
            <h2 className="font-heading text-lg font-bold">
              {mode === "create" ? t("admin.usersPage.modalCreate") : mode === "edit" ? t("admin.usersPage.modalEdit") : t("admin.usersPage.modalView")}
            </h2>
            {detail && mode !== "create" && <p className="text-xs font-mono text-muted-foreground mt-0.5">{detail.email}</p>}
          </div>
          <div className="flex items-center gap-2">
            {mode === "view" && (
              <Button size="sm" variant="outline" className="rounded-sm gap-1.5 text-xs h-7" onClick={() => setMode("edit")} data-testid="edit-user-btn">
                <Pencil className="w-3 h-3" /> {t("common.edit")}
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={onClose} data-testid="close-modal-btn"><X className="w-4 h-4" /></Button>
          </div>
        </div>
        <div className="p-6 space-y-5">
          {loading ? (
            <div className="py-12 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              {mode === "view" && detail && (
                <>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-sm bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center text-brand-primary font-heading font-bold text-xl flex-shrink-0">
                      {detail.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="font-semibold text-base">{detail.name}</div>
                      <div className="text-xs font-mono text-muted-foreground">{detail.email}</div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className={`text-xs font-mono px-2 py-0.5 rounded-sm border ${detail.role === "admin" ? "border-brand-primary/30 text-brand-primary bg-brand-primary/5" : "border-border text-muted-foreground"}`}>{detail.role}</span>
                        {planBadge(detail.plan)}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: t("admin.usersPage.analyses"), value: detail.analyses_count || 0 },
                      { label: t("admin.usersPage.usage"), value: detail.usage_count || 0 },
                      { label: t("admin.usersPage.credits"), value: detail.credits_balance || 0 },
                    ].map(s => (
                      <div key={s.label} className="rounded-sm border border-border bg-card p-3 text-center">
                        <div className="font-heading text-xl font-bold">{s.value}</div>
                        <div className="text-xs font-mono text-muted-foreground">{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-1.5 text-sm">
                    {[
                      [t("admin.usersPage.authType"), detail.auth_type || "email"],
                      [t("admin.usersPage.joined"), detail.created_at ? new Date(detail.created_at).toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta", day: "numeric", month: "long", year: "numeric" }) : "\u2014"],
                      [t("admin.usersPage.usageReset"), detail.usage_reset_at ? new Date(detail.usage_reset_at).toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta" }) : "\u2014"],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="font-mono text-xs">{v}</span></div>
                    ))}
                  </div>
                  {detail.recent_analyses?.length > 0 && (
                    <div>
                      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">{t("admin.usersPage.recentAnalyses")}</div>
                      <div className="space-y-1.5">
                        {detail.recent_analyses.map(a => (
                          <div key={a.id} className="flex items-center justify-between p-2 rounded-sm bg-secondary/30 border border-border">
                            <span className="text-xs truncate max-w-[200px]">{a.video_name}</span>
                            <span className={`text-xs font-mono font-semibold ${a.verdict === "FAKE" ? "text-verdict-fake" : a.verdict === "REAL" ? "text-verdict-real" : "text-verdict-uncertain"}`}>{a.verdict} {Math.round(a.confidence || 0)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {detail.recent_transactions?.length > 0 && (
                    <div>
                      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">{t("admin.usersPage.recentTx")}</div>
                      <div className="space-y-1.5">
                        {detail.recent_transactions.map((tx, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded-sm bg-secondary/30 border border-border">
                            <div><span className="text-xs font-medium capitalize">{tx.type || "subscription"}</span><span className="text-xs text-muted-foreground ml-1.5">{tx.plan_id || `${tx.credits} ${t("admin.usersPage.credits").toLowerCase()}`}</span></div>
                            <div className="text-right">
                              <div className="text-xs font-semibold text-verdict-real">{fmt(tx.amount)}</div>
                              <div className={`text-[10px] font-mono ${tx.payment_status === "paid" ? "text-verdict-real" : "text-muted-foreground"}`}>{tx.payment_status}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              {(mode === "edit" || mode === "create") && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider block mb-1.5">{t("admin.usersPage.lblName")}</label>
                    <input value={form.name} onChange={e => set("name", e.target.value)} placeholder={t("admin.usersPage.phName")}
                      className="w-full bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary" data-testid="modal-name-input" />
                  </div>
                  {mode === "create" && (
                    <>
                      <div>
                        <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider block mb-1.5">{t("admin.usersPage.lblEmail")}</label>
                        <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder={t("admin.usersPage.phEmail")}
                          className="w-full bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary" data-testid="modal-email-input" />
                      </div>
                      <div>
                        <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider block mb-1.5">{t("admin.usersPage.lblPassword")}</label>
                        <input type="password" value={form.password} onChange={e => set("password", e.target.value)} placeholder={t("admin.usersPage.phPassword")}
                          className="w-full bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary" data-testid="modal-password-input" />
                      </div>
                    </>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider block mb-1.5">{t("admin.usersPage.lblPlan")}</label>
                      <select value={form.plan} onChange={e => set("plan", e.target.value)}
                        className="w-full text-sm font-mono bg-background border border-border rounded-sm px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-primary"
                        style={{ color: getPlanColor(form.plan) }} data-testid="modal-plan-select">
                        {selectablePlans.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider block mb-1.5">{t("admin.usersPage.lblRole")}</label>
                      <select value={form.role} onChange={e => set("role", e.target.value)}
                        className="w-full text-sm font-mono bg-background border border-border rounded-sm px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-primary" data-testid="modal-role-select">
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                      </select>
                    </div>
                  </div>
                  {mode === "edit" && (
                    <div>
                      <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider block mb-1.5">{t("admin.usersPage.lblUsageCount")}</label>
                      <input type="number" min={0} value={form.usage_count} onChange={e => set("usage_count", e.target.value)}
                        className="w-full bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary" data-testid="modal-usage-input" />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {(mode === "edit" || mode === "create") && (
          <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3 sticky bottom-0 bg-background">
            <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => mode === "edit" ? setMode("view") : onClose()}>{t("admin.usersPage.cancel")}</Button>
            <Button size="sm" className="rounded-sm bg-brand-primary text-white gap-1.5" onClick={handleSave} disabled={saving} data-testid="save-user-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {mode === "create" ? t("admin.usersPage.create") : t("admin.usersPage.save")}
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [planOptions, setPlanOptions] = useState(DEFAULT_PLANS);
  const [search, setSearch] = useState("");
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterRole, setFilterRole] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [updatingId, setUpdating] = useState(null);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPlanOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/plans/config`, { withCredentials: true });
      const plans = Array.isArray(res?.data?.plans) ? res.data.plans : [];
      const dynamic = plans.map((p) => String(p?.id || "").trim()).filter(Boolean);
      const merged = Array.from(new Set([...dynamic, "unlimited"]));
      if (merged.length > 0) {
        setPlanOptions(merged);
      }
    } catch {
      setPlanOptions(DEFAULT_PLANS);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterRole !== "all") params.role = filterRole;
      if (filterPlan !== "all") params.plan = filterPlan;
      const res = await axios.get(`${API}/admin/users`, { params, withCredentials: true });
      const data = res.data;
      setUsers(Array.isArray(data) ? data : data.users || []);
      setPage(1);
    } catch { toast.error(t("admin.usersPage.loadFail")); }
    finally { setLoading(false); }
  }, [filterPlan, filterRole, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPlanOptions(); }, [loadPlanOptions]);

  const filtered = users.filter(u => {
    const matchSearch = !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    let matchDate = true;
    if (dateFrom && u.created_at) matchDate = u.created_at >= dateFrom;
    if (dateTo && u.created_at) matchDate = matchDate && u.created_at <= dateTo + "T23:59:59";
    return matchSearch && matchDate;
  });
  const pages = Math.ceil(filtered.length / PER_PAGE) || 1;
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const updateInline = async (userId, field, value) => {
    setUpdating(userId);
    try {
      await axios.patch(`${API}/admin/users/${userId}`, { [field]: value }, { withCredentials: true });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, [field]: value } : u));
      toast.success(t("admin.usersPage.updated"));
    } catch { toast.error(t("admin.usersPage.updateFail")); }
    finally { setUpdating(null); }
  };

  const del = async (userId, email) => {
    if (!window.confirm(t("admin.usersPage.deleteConfirm", { email }))) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      setUsers(prev => prev.filter(u => u.user_id !== userId));
      toast.success(t("admin.usersPage.deleteSuccess"));
    } catch { toast.error(t("admin.usersPage.deleteFail")); }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); };
  const hasFilters = search || filterPlan !== "all" || filterRole !== "all" || dateFrom || dateTo;
  const clearFilters = () => { setSearch(""); setFilterPlan("all"); setFilterRole("all"); setDateFrom(""); setDateTo(""); };
  useEffect(() => {
    if (filterPlan !== "all" && !planOptions.includes(filterPlan)) {
      setFilterPlan("all");
    }
  }, [filterPlan, planOptions]);

  const TH = ["admin.usersPage.thUser", "admin.usersPage.thRole", "admin.usersPage.thPlan", "admin.usersPage.thUsage", "admin.usersPage.thJoined", "admin.usersPage.thActions"];

  return (
    <div className="p-6 md:p-8" data-testid="admin-users-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">{t("admin.usersPage.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {search ? t("admin.usersPage.countFiltered", { filtered: filtered.length, total: users.length }) : t("admin.usersPage.count", { count: filtered.length })}
          </p>
        </div>
        <Button size="sm" className="rounded-sm bg-brand-primary text-white gap-1.5 h-8"
          onClick={() => setModal({ userId: null, mode: "create" })} data-testid="create-user-btn">
          <Plus className="w-3.5 h-3.5" /> {t("admin.usersPage.createUser")}
        </Button>
      </div>

      {/* Filters — matching AuditLog card style */}
      <div className="mb-6 rounded-md border border-border bg-card p-4" data-testid="users-filters">
        <div className="flex flex-wrap items-end gap-3">
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
            <label className="text-xs font-mono text-muted-foreground mb-1 block">{t("admin.usersPage.searchPlaceholder")}</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder={t("admin.usersPage.searchPlaceholder")}
                className="w-full bg-background border border-border rounded-sm pl-8 pr-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
                data-testid="users-search" />
            </div>
          </form>
          <div className="min-w-[120px]">
            <label className="text-xs font-mono text-muted-foreground mb-1 block">Plan</label>
            <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)}
              className="w-full bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="filter-plan">
              <option value="all">{t("admin.usersPage.allPlans")}</option>
              {planOptions.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="min-w-[120px]">
            <label className="text-xs font-mono text-muted-foreground mb-1 block">Role</label>
            <select value={filterRole} onChange={e => setFilterRole(e.target.value)}
              className="w-full bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="filter-role">
              <option value="all">{t("admin.usersPage.allRoles")}</option>
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-mono text-muted-foreground mb-1 block">{t("admin.audit.dateFrom")}</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="users-date-from" />
          </div>
          <div>
            <label className="text-xs font-mono text-muted-foreground mb-1 block">{t("admin.audit.dateTo")}</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="users-date-to" />
          </div>
          <div className="flex items-center gap-1.5">
            {hasFilters && (
              <Button size="sm" variant="ghost" className="rounded-sm gap-1 text-xs h-8 text-muted-foreground" onClick={clearFilters} data-testid="clear-users-filters">
                <X className="w-3 h-3" /> Reset
              </Button>
            )}
            <Button size="sm" variant="outline" className="rounded-sm gap-1.5 text-xs h-8" onClick={load} disabled={loading} data-testid="users-refresh-btn">
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-mono text-muted-foreground">
          {t("admin.audit.showing", { count: paged.length, total: filtered.length })}
        </span>
      </div>

      <div className="rounded-md border border-border bg-card overflow-hidden" data-testid="admin-users-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-secondary/40">
                {TH.map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-mono uppercase tracking-widest text-muted-foreground">{t(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-8 text-center text-sm text-muted-foreground">Loading...</td></tr>
              ) : paged.map(u => (
                <tr key={u.user_id} className="border-b border-border last:border-0 hover:bg-secondary/20 transition-colors" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <button className="text-left hover:opacity-80 transition-opacity" onClick={() => setModal({ userId: u.user_id, mode: "view" })} data-testid={`view-user-${u.user_id}`}>
                      <div className="text-sm font-medium truncate max-w-[160px] hover:text-brand-primary transition-colors">{u.name}</div>
                      <div className="text-xs font-mono text-muted-foreground truncate max-w-[160px]">{u.email}</div>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <select value={u.role || "user"} onChange={e => updateInline(u.user_id, "role", e.target.value)} disabled={updatingId === u.user_id}
                      className="text-xs font-mono bg-secondary border border-border rounded-sm px-2 py-1" data-testid={`role-select-${u.user_id}`}>
                      <option value="user">user</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select value={u.plan || "free"} onChange={e => updateInline(u.user_id, "plan", e.target.value)} disabled={updatingId === u.user_id}
                      className="text-xs font-mono bg-secondary border border-border rounded-sm px-2 py-1" style={{ color: getPlanColor(u.plan || "free") }} data-testid={`plan-select-${u.user_id}`}>
                      {planOptions.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">{u.usage_count || 0}</td>
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta" }) : "\u2014"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm text-muted-foreground hover:text-brand-primary"
                        onClick={() => setModal({ userId: u.user_id, mode: "view" })} data-testid={`detail-user-${u.user_id}`}><Eye className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm text-muted-foreground hover:text-foreground"
                        onClick={() => setModal({ userId: u.user_id, mode: "edit" })} data-testid={`edit-user-row-${u.user_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      {u.user_id !== currentUser?.user_id && (
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
                          onClick={() => del(u.user_id, u.email)} data-testid={`delete-user-${u.user_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && paged.length === 0 && (
                <tr><td colSpan={6} className="py-12 text-center text-sm text-muted-foreground">
                  {search ? t("admin.usersPage.noMatch") : t("admin.usersPage.noUsers")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pages={pages} onPage={setPage} />
      </div>

      <AnimatePresence>
        {modal && <UserModal userId={modal.userId} mode={modal.mode} onClose={() => setModal(null)} onSaved={load} t={t} planOptions={planOptions} />}
      </AnimatePresence>
    </div>
  );
}
